import React, { useEffect, useCallback, useState } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import { Divider, Flex, Button, Spin, Form, Space, message } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import styles from "./ExperienceBooking.module.css"
import { routes } from "router"
import { BOOKING_MODES } from "pagesV2/pro/Bookings/constants"
import GuestsSelector from "./GuestSelector"
import DateSelector from "./DateSelector"
import { fetchExperienceBookingDetails, updateBookingSelections, resetBookingSelections, updateEmbeddededBooking } from "store/experiences"
import { oldRoutes } from "router/old-routes"
import { numberToCurrency } from "helpers/string"
import FlatRatePriceTooltip from "ui/FlatRatePriceTooltip"
import { computeDiscountAmount } from "utils/discountUtils"
import { getCardFeeFraction } from "utils/bookingCalculations"
import PricingDetailsTooltip from "ui/PricingDetailsTooltip"
import { Text } from "ui/Typography"
import PromoCodeInput from "ui/PromoCodeInput"
import AddOnsSelector from "components/bookings/AddOnsSelector"
import { buildAddOnSelectionsAttributes } from "components/bookings/helpers"
import { getApplicableDayAdjustments } from "pagesV2/pro/Bookings/helpers/calculateBookingTotal"
import { useDevice } from "hooks/useDevice"
import { Icon } from "ui/Icon"

const INFO_DATA = [
  { label: "Date", value: "Add Date" },
  { label: "Guests", value: "Add Guests" },
  { label: "Trip Length", value: "Add Trip Length" },
  { label: "Time", value: "Add Time" }
]

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PillSelector = ({ label, options, selectedValue, onSelect, error, errorMessage, activeTab, embedded }) => {
  const isDisabled = activeTab === 2

  if (isDisabled) {
    return (
      <Flex className={styles.inputContainer}>
        <span className={styles.inputLabel}>{label}</span>
        <span className={styles.selectedInputLink} style={{ color: "var(--color-text-secondary-dark)" }}>
          {selectedValue || "—"}
        </span>
      </Flex>
    )
  }

  return (
    <div className={styles.stepFadeIn}>
      <div className={styles.stepSection}>
        <span className={styles.stepSectionLabel}>{label}</span>
        <div className={styles.pillContainer + " " + (embedded ? styles.embeddedPillContainer : "")}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${styles.pill} ${selectedValue === option.value ? styles.selected : ""}`}
              onClick={() => onSelect(option)}
            >
              {option.value}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <Text color="var(--color-error)" style={{ fontSize: "var(--font-size-s)" }}>
          {errorMessage}
        </Text>
      )}
    </div>
  )
}

PillSelector.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })).isRequired,
  selectedValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  activeTab: PropTypes.number,
  embedded: PropTypes.bool
}

const TimeSlotSelector = ({ slots, selectedSlot, onSelect, error, activeTab, embedded }) => {
  const isDisabled = activeTab === 2

  if (isDisabled) {
    return (
      <Flex className={styles.inputContainer}>
        <span className={styles.inputLabel}>Time</span>
        <span className={styles.selectedInputLink} style={{ color: "var(--color-text-secondary-dark)" }}>
          {selectedSlot || "—"}
        </span>
      </Flex>
    )
  }

  return (
    <div className={styles.stepFadeIn}>
      <div className={styles.stepSection}>
        <span className={styles.stepSectionLabel}>Time</span>
        {slots.length ? (
          <div className={styles.pillContainer}>
            {slots.map((slot) => (
              <button
                key={slot}
                type="button"
                className={`${styles.pill} ${selectedSlot === slot ? styles.selected : ""} ${embedded ? styles.embeddedTimePillContainer : ""}`}
                onClick={() => onSelect(slot)}
              >
                {slot}
              </button>
            ))}
          </div>
        ) : (
          <Text style={{ color: "var(--color-text-secondary-dark)" }}>No Time Slots</Text>
        )}
      </div>
      {error && (
        <Text color="var(--color-error)" style={{ fontSize: "var(--font-size-s)" }}>
          Time is required.
        </Text>
      )}
    </div>
  )
}

TimeSlotSelector.propTypes = {
  slots: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedSlot: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.bool,
  activeTab: PropTypes.number,
  embedded: PropTypes.bool
}

const PriceBreakdown = ({
  breakdown,
  amount,
  depositAmount,
  balanceAmount,
  selectedPayment,
  bookingDate,
  appliedPromos = [],
  baseAmount = 0,
  addOnsTotal = 0
}) => {
  // amount is already net of the discount (applied before tax in calculateBookingTotal); the
  // promo rows below are shown for transparency only and must NOT be subtracted again.
  const adjustedTotal = Math.max(0, amount ?? 0)

  return (
    <>
      <Flex vertical gap={4}>
        {breakdown?.map((item) => (
          <Flex key={`${item?.label}-${item?.amount}`} align="center" justify="space-between">
            <Flex align="center" gap={4}>
              <Text>{item?.label}</Text>
              {item?.pricingDetails && <FlatRatePriceTooltip {...item.pricingDetails} />}
            </Flex>
            <Text>{item?.amount}</Text>
          </Flex>
        ))}
        {appliedPromos.map((promo) => (
          <Flex key={promo.code} align="center" justify="space-between">
            <Text type="secondary">
              {promo.name} ({promo.code})
            </Text>
            <Text className={styles.discountAmount}>-{numberToCurrency(computeDiscountAmount(promo, baseAmount, addOnsTotal))}</Text>
          </Flex>
        ))}
      </Flex>
      <Divider />
      <Flex align="center" justify="space-between">
        <Text strong style={{ fontWeight: 600 }}>
          Total
        </Text>
        <Text style={{ fontWeight: 600 }}>{numberToCurrency(adjustedTotal)}</Text>
      </Flex>
      {selectedPayment && selectedPayment !== "full" && (
        <>
          <Flex align="center" justify="space-between">
            <Text strong style={{ fontWeight: 600 }}>
              Pay Now (Deposit)
            </Text>
            <Text style={{ fontWeight: 600 }}>{numberToCurrency(depositAmount)}</Text>
          </Flex>
          <Flex align="center" justify="space-between">
            <Text>
              {selectedPayment === "later"
                ? `Balance Due on ${moment(bookingDate).subtract(1, "day").format("MMMM D, YYYY")}`
                : `Balance Due on ${moment(bookingDate).format("MMMM D, YYYY")}`}
            </Text>
            <Text>{numberToCurrency(balanceAmount || 0)}</Text>
          </Flex>
        </>
      )}
    </>
  )
}

PriceBreakdown.propTypes = {
  breakdown: PropTypes.array,
  amount: PropTypes.number,
  depositAmount: PropTypes.number,
  balanceAmount: PropTypes.number,
  selectedPayment: PropTypes.string,
  bookingDate: PropTypes.string,
  appliedPromos: PropTypes.array,
  baseAmount: PropTypes.number,
  addOnsTotal: PropTypes.number
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const BookingDetailCard = ({
  bookNow,
  trip,
  bookingButtonVisible,
  activeTab,
  bookingErrors,
  setBookingErrors,
  form,
  mode,
  embedded,
  appliedPromos,
  onPromoApplied,
  onPromoRemoved,
  clientIdentifier,
  isEditingAddOns,
  setIsEditingAddOns,
  addOnsSnapshot,
  setAddOnsSnapshot
}) => {
  const [slotsFetching, setSlotsFetching] = useState(false)
  const { isMobile } = useDevice()
  // On mobile, once the user is on the booking page (activeTab === 1) the date/time/guest selectors
  // have already been chosen, so we collapse them by default to a compact summary. This keeps the
  // tall card from pushing the Contact Information form (rendered below it on mobile) out of view.
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const selectedPayment = Form.useWatch("paymentMethod", form)
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const location = useLocation()
  const bookingId = location.state?.b_uuid
  const dispatch = useDispatch()
  const [t] = useTranslation()
  const isBookNow = location?.pathname?.includes("/experience_detail") || location?.pathname?.includes("/trips")

  const {
    experienceById: { experienceDetail },
    experienceBookingData,
    experienceBookingData: { bookingSelections },
    bookingInfo
  } = useSelector((state) => state.experiences)

  const userRole = useSelector((state) => state.user.role)

  // Multi-day experiences have no duration-selection step and no per-slot time selection — the trip
  // runs across a fixed set of dates with a fixed start time. We seed tripLength from trip_prices[0]
  // (mirroring the guide-side manual flow) and go straight from date → guests.
  const durationType = experienceDetail?.duration_type
  const durationValue = experienceDetail?.duration_value
  const isMultiDay = durationType === "multi_day"
  const minGuests = experienceBookingData?.bookingData?.min_guests ?? experienceDetail?.min_guests
  const maxGuests = experienceBookingData?.bookingData?.max_guests ?? experienceDetail?.max_guests

  // The card processing fee is taken on the full total EXCEPT for deposit-now + balance-in-cash
  // ("cash"), where only the deposit is paid by card so the fee applies to the deposit only.
  const depositFraction = getCardFeeFraction({
    depositOnly: !!(selectedPayment && selectedPayment !== "full"),
    depositPercent: experienceDetail?.deposit_percentage,
    balanceInCash: selectedPayment === "cash"
  })

  // bookingSelections.amount is the grand total (incl the card fee). preCardTotal is the total
  // before any card fee — the base the deposit % is taken from; cardProcessingFee is the card fee
  // charged up front with the deposit (full fee for balance-by-card, deposit-only fee for cash).
  const preCardTotal = Math.max(0, bookingSelections?.preCardTotal ?? bookingSelections?.amount ?? 0)
  const cardProcessingFee = Math.max(0, bookingSelections?.cardProcessingFee || 0)
  const depositBase = (preCardTotal * (experienceDetail?.deposit_percentage || 0)) / 100
  const depositAmount = depositBase + cardProcessingFee
  // The card fee is collected up front, so the remaining balance carries no card fee.
  const balanceAmount = preCardTotal - depositBase

  const calculateBookingTotal = useCallback(
    (counts, customFees = [], discountPromos = []) => {
      let total = 0
      const breakdown = []

      const priceData = bookingSelections?.tripLength?.price || {}
      const {
        pricing_type,
        flat_rate,
        price_per_person,
        price_per_child,
        price_per_infant,
        price_per_passenger,
        included_guests,
        max_guests
      } = priceData

      const getPrice = (specificPrice) => Number.parseFloat(specificPrice ?? 0)

      // Fold any matching day adjustments (e.g. weekend surcharges) directly into the base unit
      // prices before computing totals, so they apply to the flat rate / per-person rates and to
      // extra-guest per-person rates alike.
      const applicableDayAdjustments = getApplicableDayAdjustments(experienceDetail?.day_adjustments, bookingSelections?.date)
      const adjustPrice = (price) => {
        let adjusted = getPrice(price)
        for (const adjustment of applicableDayAdjustments) {
          const rawAmount =
            adjustment.price_type === "percent"
              ? (adjusted * (Number(adjustment.price_value) || 0)) / 100
              : Number(adjustment.price_value) || 0
          adjusted += adjustment.direction === "decrease" ? -rawAmount : rawAmount
        }
        // A flat/fixed "decrease" adjustment can exceed the base price — never let it go negative.
        return Math.max(0, adjusted)
      }

      const adjustedFlatRate = adjustPrice(flat_rate)
      const adjustedPricePerPerson = adjustPrice(price_per_person)
      const adjustedPricePerChild = adjustPrice(price_per_child)
      const adjustedPricePerInfant = adjustPrice(price_per_infant)
      const adjustedPricePerPassenger = adjustPrice(price_per_passenger)

      const totalCounts = counts?.main || { adults: 0, children: 0, infants: 0, passengers: 0 }

      if (pricing_type === "charge_flat_rate") {
        const flatAmount = adjustedFlatRate
        const totalGuests = totalCounts.adults + totalCounts.children + totalCounts.infants + totalCounts.passengers
        const hasGuests = totalGuests > 0 || totalCounts.passengers > 0

        if (hasGuests) {
          total += flatAmount
          const guestsIncluded = included_guests ?? max_guests ?? 0
          let remainingCapacity = guestsIncluded

          const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
          remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)

          const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
          remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)

          const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)

          const extraAdults = Math.max(0, totalCounts.adults - adultsIncluded)
          const extraChildren = Math.max(0, totalCounts.children - childrenIncluded)
          const extraInfants = Math.max(0, totalCounts.infants - infantsIncluded)

          breakdown.push({
            label: "Flat Rate",
            amount: numberToCurrency(flatAmount),
            pricingDetails: {
              pricing_type,
              included_guests: guestsIncluded,
              price_per_person: adjustedPricePerPerson,
              price_per_child: adjustedPricePerChild,
              price_per_infant: adjustedPricePerInfant
            }
          })

          if (extraAdults > 0) {
            const lineTotal = extraAdults * adjustedPricePerPerson
            total += lineTotal
            breakdown.push({ label: `Extra Adult x ${extraAdults}`, amount: numberToCurrency(lineTotal) })
          }
          if (extraChildren > 0) {
            const lineTotal = extraChildren * adjustedPricePerPerson
            total += lineTotal
            breakdown.push({ label: `Extra Child x ${extraChildren}`, amount: numberToCurrency(lineTotal) })
          }
          if (extraInfants > 0) {
            const lineTotal = extraInfants * adjustedPricePerPerson
            total += lineTotal
            breakdown.push({ label: `Extra Infant x ${extraInfants}`, amount: numberToCurrency(lineTotal) })
          }
          if (totalCounts.passengers > 0) {
            const lineTotal = totalCounts.passengers * adjustedPricePerPerson
            total += lineTotal
            breakdown.push({ label: `Passenger x ${totalCounts.passengers}`, amount: numberToCurrency(lineTotal) })
          }
        }
      } else {
        const mapping = [
          { count: totalCounts.adults, label: "Adult", price: adjustedPricePerPerson },
          { count: totalCounts.children, label: "Child", price: adjustedPricePerChild },
          { count: totalCounts.infants, label: "Infant", price: adjustedPricePerInfant },
          { count: totalCounts.passengers, label: "Passenger", price: adjustedPricePerPassenger }
        ]

        for (const { count, label, price } of mapping) {
          if (count > 0) {
            const lineTotal = count * price
            total += lineTotal
            breakdown.push({ label: `${label} x ${count}`, amount: numberToCurrency(lineTotal) })
          }
        }
      }

      // Guest-only subtotal (used as discount base when promo does NOT apply to add-ons)
      const guestSubtotal = total

      // Add-on lines (one per selected add-on)
      const addOnSelections = bookingSelections?.addOns || {}
      const tripAddOns = trip?.add_ons || []
      let addOnsTotal = 0
      for (const a of tripAddOns) {
        const qty = Number(addOnSelections[a.id] || 0)
        if (qty > 0) {
          const lineTotal = qty * Number(a.price ?? 0)
          total += lineTotal
          addOnsTotal += lineTotal
          breakdown.push({ label: `${a.title} × ${qty}`, amount: numberToCurrency(lineTotal) })
        }
      }

      // Apply the discount to the pre-tax base BEFORE computing tax & fees, matching the backend.
      // Each promo's apply_to_add_ons flag decides whether add-ons are part of its discountable base.
      const rawDiscount = (discountPromos ?? []).reduce(
        (sum, promo) => sum + computeDiscountAmount(promo, guestSubtotal, addOnsTotal),
        0
      )
      const discountAmount = Math.max(0, Math.min(rawDiscount, guestSubtotal + addOnsTotal))
      total -= discountAmount

      // Taxable base = (guests + add-ons) net of discount (fees apply to the discounted amount)
      const taxableBase = guestSubtotal + addOnsTotal - discountAmount

      // "Card Processing Fee" must be applied last on the running total (not on taxableBase).
      // All other fees are applied on taxableBase first.
      let hiddenFeesTotal = 0
      let cardProcessingFee = 0

      if (taxableBase > 0 && customFees?.length > 0) {
        // The card processing fee is only passed on to the guest when the experience's
        // platform_fee_setting opts in via card_fee_passthrough_enabled.
        const cardFeePassthroughEnabled = experienceDetail?.platform_fee_setting?.card_fee_passthrough_enabled === true
        const cardFeeEntry = cardFeePassthroughEnabled ? customFees.find((f) => f.title === "Card Processing Fee") : null
        const regularFees = customFees.filter((f) => f.title !== "Card Processing Fee")

        for (const fee of regularFees) {
          const feeAmount = fee.fee_type === "percentage" ? (taxableBase * Number(fee.price)) / 100 : Number(fee.price)
          total += feeAmount
          if (fee.show_as_line_item) {
            breakdown.push({ label: fee.title, amount: numberToCurrency(feeAmount) })
          } else {
            hiddenFeesTotal += feeAmount
          }
        }

        // Card processing fee applied last. It's charged only on the amount paid by card NOW: the
        // deposit (depositFraction of the pre-card total) for deposit payments, or the whole
        // pre-card total when paying in full. At this point `total` is the pre-card total.
        // Gross-up: divide by (1 - rate) so that after Stripe deducts its fee on (depositBase +
        // fee), the net equals depositBase. A plain markup would under-collect by the fee-on-fee.
        if (cardFeeEntry && total > 0) {
          const cardRate = Number(cardFeeEntry.price) / 100
          const depositBaseForFee = total * depositFraction
          cardProcessingFee = (depositBaseForFee * cardRate + 0.3) / (1 - cardRate)
          total += cardProcessingFee
        }

        const taxAndFeesTotal = hiddenFeesTotal + (cardFeeEntry && !cardFeeEntry.show_as_line_item ? cardProcessingFee : 0)
        if (taxAndFeesTotal > 0) {
          breakdown.push({ label: "Tax & Fees", amount: numberToCurrency(taxAndFeesTotal) })
        }

        if (cardFeeEntry?.show_as_line_item && cardProcessingFee > 0) {
          breakdown.push({ label: "Card Processing Fee", amount: numberToCurrency(cardProcessingFee) })
        }
      }

      return { total, guestSubtotal, addOnsTotal, discountAmount, breakdown, preCardTotal: total - cardProcessingFee }
    },
    [
      bookingSelections?.tripLength?.price,
      bookingSelections?.addOns,
      bookingSelections?.date,
      trip?.add_ons,
      experienceDetail?.platform_fee_setting?.card_fee_passthrough_enabled,
      experienceDetail?.day_adjustments,
      depositFraction
    ]
  )

  useEffect(() => {
    const tripPricing = bookingSelections?.tripLength?.price
    const counts = bookingSelections?.counts
    if (tripPricing && counts) {
      const { total, guestSubtotal, addOnsTotal, breakdown, preCardTotal: preCard } = calculateBookingTotal(
        counts,
        experienceDetail?.assigned_custom_fees,
        appliedPromos
      )
      dispatch(updateBookingSelections({ amount: total, baseAmount: guestSubtotal, addOnsTotal, breakdown, preCardTotal: preCard, cardProcessingFee: total - preCard }))
    }
  }, [
    bookingSelections?.tripLength,
    bookingSelections?.counts,
    bookingSelections?.addOns,
    appliedPromos,
    calculateBookingTotal,
    dispatch,
    experienceDetail?.assigned_custom_fees
  ])

  const handleAddOnsChange = useCallback(
    (e) => {
      dispatch(updateBookingSelections({ addOns: e?.target?.value || {} }))
    },
    [dispatch]
  )

  const [isAddOnsSaving, setIsAddOnsSaving] = useState(false)

  const handleStartEditAddOns = () => {
    setAddOnsSnapshot?.({ ...(bookingSelections?.addOns || {}) })
    setIsEditingAddOns?.(true)
  }

  const handleCancelEditAddOns = () => {
    if (addOnsSnapshot) {
      dispatch(updateBookingSelections({ addOns: addOnsSnapshot }))
    }
    setAddOnsSnapshot?.(null)
    setIsEditingAddOns?.(false)
  }

  const handleSaveAddOns = async () => {
    const current = bookingSelections?.addOns || {}
    const isUnchanged = JSON.stringify(current) === JSON.stringify(addOnsSnapshot || {})
    if (isUnchanged) {
      setIsEditingAddOns?.(false)
      setAddOnsSnapshot?.(null)
      return
    }
    const bookingId = bookingInfo?.data?.uuid
    if (!bookingId) {
      setIsEditingAddOns?.(false)
      setAddOnsSnapshot?.(null)
      return
    }
    const attrs = buildAddOnSelectionsAttributes(current, bookingInfo?.data?.booking_add_ons)
    setIsAddOnsSaving(true)
    try {
      const res = await dispatch(updateEmbeddededBooking(bookingId, { booking: { booking_add_ons_attributes: attrs } }))
      if (res?.uuid) {
        setIsEditingAddOns?.(false)
        setAddOnsSnapshot?.(null)
      }
    } catch (err) {
      message.error(err?.data?.message || t("booking.add_ons.update_failed"))
    } finally {
      setIsAddOnsSaving(false)
    }
  }

  const addOnsHeaderExtra =
    activeTab === 2 ? (
      isEditingAddOns ? (
        <Space size={8}>
          <Button size="small" onClick={handleCancelEditAddOns} disabled={isAddOnsSaving}>
            {t("global.cancel")}
          </Button>
          <Button size="small" type="primary" onClick={handleSaveAddOns} loading={isAddOnsSaving}>
            {t("global.save")}
          </Button>
        </Space>
      ) : (
        <Button size="small" type="link" onClick={handleStartEditAddOns} className={styles.editLinkButton}>
          {t("global.edit")}
        </Button>
      )
    ) : null


  // Multi-day: seed tripLength from trip_prices[0] as soon as the experience detail loads so pricing
  // (flat_rate / per-person) and guest-allowed flags are available without a duration step.
  useEffect(() => {
    if (!isMultiDay) return
    const tripPrice = experienceDetail?.trip_prices?.[0]
    if (!tripPrice) return
    dispatch(updateBookingSelections({ tripLength: { key: tripPrice.id, value: "", price: tripPrice } }))
  }, [experienceDetail?.id, experienceDetail?.trip_prices, isMultiDay, dispatch])

  const handleDateChange = useCallback(
    (value) => {
      const formattedDate = dayjs(value).format("YYYY-MM-DD")

      // Multi-day has no duration/time-slot step — keep the seeded tripLength and go straight to guests.
      if (isMultiDay) {
        const tripPrice = experienceDetail?.trip_prices?.[0]
        dispatch(
          updateBookingSelections({
            date: formattedDate,
            tripLength: tripPrice ? { key: tripPrice.id, value: "", price: tripPrice } : bookingSelections?.tripLength,
            timeSlot: null
          })
        )
        if (setBookingErrors) {
          setBookingErrors((prev) => ({ ...prev, date: false, tripLength: false, timeSlot: false, counts: false }))
        }
        return
      }

      const tripDurations = experienceBookingData?.bookingData?.trip_durations
      const autoTripLength = tripDurations?.length === 1 ? tripDurations[0] : null

      dispatch(
        updateBookingSelections({
          date: formattedDate,
          tripLength: autoTripLength,
          timeSlot: null
        })
      )

      if (autoTripLength?.key) {
        setSlotsFetching(true)
        dispatch(fetchExperienceBookingDetails(id, "available_slots", autoTripLength.key, formattedDate, "", mode))
      }

      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, date: false, tripLength: false, timeSlot: false, counts: false }))
      }
    },
    [
      dispatch,
      id,
      mode,
      setBookingErrors,
      experienceBookingData?.bookingData?.trip_durations,
      isMultiDay,
      experienceDetail?.trip_prices,
      bookingSelections?.tripLength
    ]
  )

  const handleTripLengthChange = useCallback(
    (tripLength) => {
      const hasCounts = Object.values(bookingSelections?.counts?.main ?? {}).some((v) => v > 0)
      const minCount = experienceBookingData?.bookingData?.min_guests || 0
      const price = tripLength?.price || {}
      const fieldOrder = [
        { key: "adults", allowed: price.adults_allowed !== false },
        { key: "children", allowed: !!price.children_allowed },
        { key: "infants", allowed: !!price.infants_allowed },
        { key: "passengers", allowed: !!price.passengers_allowed }
      ]
      const firstAllowed = fieldOrder.find((f) => f.allowed)?.key || "adults"
      dispatch(
        updateBookingSelections({
          tripLength,
          timeSlot: null,
          counts: hasCounts
            ? bookingSelections?.counts
            : {
                main: {
                  adults: firstAllowed === "adults" ? minCount : 0,
                  children: firstAllowed === "children" ? minCount : 0,
                  infants: firstAllowed === "infants" ? minCount : 0,
                  passengers: firstAllowed === "passengers" ? minCount : 0
                },
                extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
              }
        })
      )

      if (tripLength?.key && bookingSelections?.date) {
        setSlotsFetching(true)
        dispatch(
          fetchExperienceBookingDetails(
            id,
            "available_slots",
            tripLength?.key,
            dayjs(bookingSelections?.date).format("YYYY-MM-DD"),
            "",
            mode
          )
        )
      }

      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, tripLength: false }))
      }
    },
    [
      dispatch,
      bookingSelections?.date,
      bookingSelections?.counts,
      mode,
      id,
      setBookingErrors,
      experienceBookingData?.bookingData?.min_guests
    ]
  )

  const handleTimeSlotChange = useCallback(
    (value) => {
      dispatch(updateBookingSelections({ timeSlot: value }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, timeSlot: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  const handleGuestsChange = useCallback(
    (newCounts) => {
      dispatch(updateBookingSelections({ counts: newCounts }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, counts: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  const handleBookNow = useCallback(() => {
    if (!isBookNow) return

    const { date, counts, tripLength, timeSlot } = bookingSelections
    const totalGuests = ["adults", "children", "infants", "passengers"]
      .map((key) => Number(counts?.main?.[key] || 0))
      .reduce((acc, val) => acc + val, 0)
    const errors = {
      date: !date,
      counts: !counts?.main || totalGuests < (minGuests || 1),
      tripLength: !tripLength,
      timeSlot: isMultiDay ? false : !timeSlot
    }
    const hasErrors = Object.values(errors).some(Boolean)

    if (hasErrors) {
      setBookingErrors(errors)
      return
    }

    setBookingErrors({ date: false, counts: false, tripLength: false, timeSlot: false })

    if (mode === BOOKING_MODES.embedded || !mode) {
      navigate(routes.embeddedExperienceBooking({ uuid, id }))
    } else {
      const isClientUser = userRole === "user"
      if (isClientUser) navigate(routes.clientBookingWizardRootPath({ id }))
      else navigate({ pathname: oldRoutes.bookingWizardPath({ id }) })
    }
  }, [isBookNow, bookingSelections, minGuests, isMultiDay, setBookingErrors, mode, userRole, navigate, id, uuid])

  useEffect(() => {
    if (bookNow) {
      if (id) {
        dispatch(fetchExperienceBookingDetails(id, "trip_data", "", "", bookingId, mode))
      }
      if (isBookNow) {
        dispatch(resetBookingSelections())
      }
    }
  }, [bookNow, dispatch, id, bookingId, mode, isBookNow])

  useEffect(() => {
    if (isMultiDay) return
    const tripDurations = experienceBookingData?.bookingData?.trip_durations
    if (
      tripDurations?.length === 1 &&
      (!bookingSelections?.tripLength || bookingSelections?.tripLength?.value !== tripDurations?.[0]?.value)
    ) {
      handleTripLengthChange(tripDurations?.[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceBookingData?.bookingData?.trip_durations, isMultiDay])

  useEffect(() => {
    if (isMultiDay) return
    const timeSlots = experienceBookingData?.bookingData?.data
    if (timeSlots?.length === 1 && !bookingSelections?.timeSlot && !experienceBookingData?.loading) {
      handleTimeSlotChange(timeSlots[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceBookingData?.bookingData?.data, experienceBookingData?.loading, bookingSelections?.timeSlot, isMultiDay])

  useEffect(() => {
    if (!experienceBookingData?.loading) {
      setSlotsFetching(false)
    }
  }, [experienceBookingData?.loading])

  const tripDurations = experienceBookingData?.bookingData?.trip_durations
  const hasMultipleDurations = tripDurations?.length > 1
  // Multi-day: no trip-length pill, no time-slot step — go straight from date to guests.
  const showTripLength = !isMultiDay && !!bookingSelections?.date && hasMultipleDurations
  const showTime =
    !isMultiDay && !!bookingSelections?.date && !!bookingSelections?.tripLength && !experienceBookingData?.loading && !slotsFetching
  const showGuests = isMultiDay
    ? !!bookingSelections?.date && !!bookingSelections?.tripLength
    : showTime && !!bookingSelections?.timeSlot
  const timeSlots = experienceBookingData?.bookingData?.data ?? []

  const canCollapse = bookNow && isMobile && activeTab === 1 && showGuests
  const isCollapsed = canCollapse && !detailsExpanded

  const infoRows = INFO_DATA.map((data, index) => (
    <Flex justify="space-between" align="center" className={`${styles.cardDetailItems} p-s rounded-s`} key={index + 1}>
      <Text>{data.label}</Text>
      <Text>{data.value}</Text>
    </Flex>
  ))

  const fullSelectors = (
    <>
      <DateSelector
        value={bookingSelections?.date ? dayjs(bookingSelections?.date) : null}
        onChange={handleDateChange}
        activeTab={activeTab}
        error={bookingErrors?.date}
        trip={trip}
      />
      {showTripLength && (
        <PillSelector
          key="trip-length"
          label="Trip Length"
          options={tripDurations}
          selectedValue={bookingSelections.tripLength?.value}
          onSelect={handleTripLengthChange}
          error={bookingErrors?.tripLength}
          errorMessage="Trip Length is required."
          activeTab={activeTab}
          embedded
        />
      )}
      {showTime && (
        <TimeSlotSelector
          key="time-slot"
          slots={timeSlots}
          selectedSlot={bookingSelections?.timeSlot}
          onSelect={handleTimeSlotChange}
          error={bookingErrors?.timeSlot}
          activeTab={activeTab}
          embedded
        />
      )}
      {showGuests && (
        <div key="guests" className={styles.stepFadeIn}>
          <GuestsSelector
            counts={bookingSelections?.counts}
            setCounts={handleGuestsChange}
            activeTab={activeTab}
            error={bookingErrors?.counts}
            setBookingErrors={setBookingErrors}
            maxGuests={maxGuests}
            minGuests={minGuests}
          />
        </div>
      )}
      {showGuests && trip?.add_ons?.length > 0 && (
        <div key="add-ons" className={styles.stepFadeIn}>
          <AddOnsSelector
            addOns={trip.add_ons}
            selections={bookingSelections?.addOns || {}}
            onChange={handleAddOnsChange}
            disabled={activeTab === 2 && !isEditingAddOns}
            headerExtra={addOnsHeaderExtra}
          />
        </div>
      )}
    </>
  )

  // Pick the body without a nested ternary: info rows (preview), nothing when collapsed (the whole
  // section folds into the header), or the full selectors.
  let detailBody = fullSelectors
  if (bookNow === false) {
    detailBody = infoRows
  } else if (isCollapsed) {
    detailBody = null
  }

  return (
    <div className={styles.bookingDetailCardSpinner}>
      <Spin spinning={experienceBookingData?.loading}>
        <div className={`${styles.bookingCard} ${embedded ? styles.embeddedBookingCard : ""}`}>
          {!isMultiDay && !hasMultipleDurations && tripDurations?.length === 1 && <Text size="lg">{tripDurations[0]?.value}</Text>}
          <Flex gap={8} align="center" justify="space-between">
            <Flex gap={8} align="center">
              <Text fw={600} size="lg">
                {bookNow ? (trip?.title ?? "") : `From ${numberToCurrency(trip?.price_per_person)}`}
              </Text>
              <PricingDetailsTooltip tripPrices={trip?.trip_prices} durationType={durationType} durationValue={durationValue} />
            </Flex>
            {canCollapse && (
              <Button
                type="text"
                aria-label={isCollapsed ? "Expand booking details" : "Collapse booking details"}
                className={styles.collapseToggle}
                icon={<Icon type="feather" name={isCollapsed ? "ChevronDown" : "ChevronUp"} color="currentColor" width={20} height={20} />}
                onClick={() => setDetailsExpanded((prev) => !prev)}
              />
            )}
          </Flex>
          {detailBody && (
            <Flex vertical gap={8} className={embedded ? "py-xs" : "py-xl"}>
              {detailBody}
            </Flex>
          )}
          {bookNow && showGuests && !isCollapsed && (
            <>
              {activeTab === 2 && (
                <PromoCodeInput
                  totalAmount={bookingSelections?.baseAmount ?? 0}
                  addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
                  appliedPromos={appliedPromos}
                  onPromoApplied={onPromoApplied}
                  onPromoRemoved={onPromoRemoved}
                  experienceId={id}
                  clientIdentifier={clientIdentifier}
                />
              )}
              <PriceBreakdown
                breakdown={bookingSelections?.breakdown}
                amount={bookingSelections?.amount}
                depositAmount={depositAmount}
                balanceAmount={balanceAmount}
                selectedPayment={selectedPayment}
                bookingDate={bookingSelections?.date}
                appliedPromos={appliedPromos}
                baseAmount={bookingSelections?.baseAmount ?? 0}
                addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
              />
            </>
          )}
          {bookingButtonVisible && (
            <>
              <Button type="primary" disabled={!bookNow} className={styles.bookButton} onClick={handleBookNow}>
                Book Now
              </Button>
              <Flex justify="center" style={{ marginTop: "var(--spacing-xxxs)" }}>
                <Text style={{ fontSize: "var(--font-size-s)" }}>{t("webIntegration.payment_info")}</Text>
              </Flex>
            </>
          )}
        </div>
      </Spin>
    </div>
  )
}

BookingDetailCard.propTypes = {
  bookNow: PropTypes.bool,
  form: PropTypes.object,
  trip: PropTypes.shape({
    min_price: PropTypes.number,
    title: PropTypes.string,
    guide_booking_fee_percentage: PropTypes.number,
    price_per_person: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trip_prices: PropTypes.array,
    add_ons: PropTypes.array
  }),
  bookingButtonVisible: PropTypes.bool,
  activeTab: PropTypes.number,
  bookingErrors: PropTypes.object,
  setBookingErrors: PropTypes.func,
  mode: PropTypes.string,
  embedded: PropTypes.bool,
  appliedPromos: PropTypes.array,
  onPromoApplied: PropTypes.func,
  onPromoRemoved: PropTypes.func,
  clientIdentifier: PropTypes.string,
  isEditingAddOns: PropTypes.bool,
  setIsEditingAddOns: PropTypes.func,
  addOnsSnapshot: PropTypes.object,
  setAddOnsSnapshot: PropTypes.func
}

export default BookingDetailCard
